import { useAppSelector } from 'app/hooks';
import FootprintTemplateButton from 'components/structure-page/footprint-template-button';
import { PositionType } from 'data/types';
import useLayoutImageMetrics from 'hooks/use-layout-image-center';
import { useCallback, useMemo } from 'react';
import { getPresetTargetArea } from 'utils/footprint-preset-area';
import { resolvePresetShapeTarget } from 'utils/footprint-preset-placement';
import { FOOTPRINT_SHAPE_LIMIT_MESSAGE } from 'utils/footprint-shape-messages';
import {
  DEFAULT_PRESET_AREA_SQFT,
  FOOTPRINT_TEMPLATE_CATEGORIES,
  FootprintShapeVariantId,
  generateFootprintShape
} from 'utils/footprint-shapes';
import {
  getEffectiveFootprintShapes,
  translateShapeCenterTo
} from 'utils/footprint-union';
import { resolveLayoutImageMetrics } from 'utils/image-scale';

type FootprintShapeSelectorProps = Readonly<{
  canAddShape: boolean;
  onAddShape?: (shape: PositionType[]) => void;
  onShapeSelect?: () => void;
}>;

export default function FootprintShapeSelector({
  canAddShape,
  onAddShape,
  onShapeSelect
}: FootprintShapeSelectorProps) {
  const { projectSettings } = useAppSelector((state) => state.project);
  const { structure } = useAppSelector((state) => state.structure);
  const { activeLevelIndex } = useAppSelector((state) => state.ui);
  const { metrics: imageMetrics, imageInfo } = useLayoutImageMetrics();

  const totalSqft = useMemo(() => {
    const setting = projectSettings.find((ps) => ps.code === 'square_footage');
    if (!setting?.value) return 0;
    const value = Number(setting.value);
    return Number.isNaN(value) ? 0 : value;
  }, [projectSettings]);

  const activeLevel = structure[activeLevelIndex];

  const perLevelArea = useMemo(() => {
    if (!activeLevel) return DEFAULT_PRESET_AREA_SQFT;
    return getPresetTargetArea({
      structure,
      activeLevelUniqueId: activeLevel.uniqueId,
      totalSqft,
      fallbackArea: DEFAULT_PRESET_AREA_SQFT
    });
  }, [activeLevel, structure, totalSqft]);

  const handleVariantClick = useCallback(
    async (variantId: FootprintShapeVariantId) => {
      if (!activeLevel || !canAddShape) return;

      const existingShapes = getEffectiveFootprintShapes(activeLevel);
      const metrics = await resolveLayoutImageMetrics(imageInfo, imageMetrics);
      const shape = generateFootprintShape(variantId, perLevelArea);
      const target = resolvePresetShapeTarget(
        shape,
        existingShapes,
        metrics?.center ?? null
      );

      onAddShape?.(translateShapeCenterTo(shape, target));
      onShapeSelect?.();
    },
    [
      perLevelArea,
      activeLevel,
      canAddShape,
      imageInfo,
      imageMetrics,
      onAddShape,
      onShapeSelect
    ]
  );

  return (
    <div className='flex flex-col gap-4'>
      {FOOTPRINT_TEMPLATE_CATEGORIES.map((category) => (
        <div key={category.id} className='flex flex-col gap-2'>
          <p className='text-sm font-medium text-gray-700'>{category.label}</p>
          <div className='grid grid-cols-3 gap-2'>
            {category.templates.map((template) => (
              <FootprintTemplateButton
                key={template.id}
                template={template}
                disabled={!canAddShape}
                disabledReason={FOOTPRINT_SHAPE_LIMIT_MESSAGE}
                onSelect={() => handleVariantClick(template.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
